<template>
  <table class="reports-table job-sites-table table">
    <tbody class="thead">
      <tr>
        <template v-if="walker.sub_show">
          <th>{{ walker.sub_label }}</th>
        </template>
        <template v-if="walker.third_show">
          <th>{{ walker.third_label }}</th>
        </template>
        <th>Enter</th>
        <th>Exit</th>
        <template v-if="walker.main_type === 'user' && totalTracked != null">
          <th>Site time</th>
          <th>Total tracked</th>
          <th>Off-site time</th>
          <th>% on-site</th>
        </template>
        <template v-else>
          <th>Time</th>
        </template>
      </tr>
    </tbody>
    <!-- eslint-disable vue/require-v-for-key -->
    <template v-for="section in tableData.sections">
      <tbody class="grouping-header">
        <tr>
          <th>
            <template v-if="walker.main_type === 'date'">
              {{ format_date_label(section.main) }}
            </template>
            <template v-else-if="walker.main_type === 'job_site'">
              <i class="hi hi-18 hi-pin"></i>
              {{ section.main }}
            </template>
            <template v-else-if="walker.main_type === 'user'">
              <ReportTableAvatar
                :user-id="section.main_id"
                :url="section.rows[0].record.avatar_url"
              />
              {{ section.main }}
            </template>
          </th>
          <th
            v-for="col in walker.base_column_count - 1"
            :key="col"
          ></th>
        </tr>
      </tbody>
      <tbody class="tbody">
        <tr
          v-for="row in section.rows"
          :class="row_class(row.sub_show_data)"
        >
          <template v-if="walker.sub_show">
            <td v-if="row.sub_show_data">
              <template v-if="walker.sub_type === 'user'">
                <ReportTableAvatar
                  :user-id="row.record.sub_id"
                  :url="row.record.avatar_url"
                />
                {{ row.record.sub }}
              </template>
              <template v-else-if="walker.sub_type === 'job_site'">
                {{ row.record.sub }}
              </template>
              <template v-else-if="walker.sub_type === 'date'">{{ format_date_label(row.record.sub) }}</template>
              <template v-else>{{ row.record.sub }}</template>
            </td>
            <td v-else></td>
          </template>
          <template v-if="walker.third_show">
            <td v-if="row.third_show_data">
              <template v-if="walker.third_type === 'user'">
                <ReportTableAvatar
                  :user-id="row.record.third_id"
                  :url="row.record.avatar_url"
                />
                {{ row.record.third }}
              </template>
              <template v-else-if="walker.third_type === 'job_site'">
                {{ row.record.third }}
              </template>
              <template v-else-if="walker.third_type === 'date'">{{ format_date_label(row.record.third) }}</template>
              <template v-else>{{ row.record.third }}</template>
            </td>
            <td v-else></td>
          </template>

          <template v-if="row.aggregate">
            <td></td>
            <td></td>
            <td class="duration">{{ row.aggregate.duration | formatDuration('auto', null, false, true) }}</td>
          </template>
          <template v-else>
            <td>
              <span
                v-tooltip
                :title="row.record.enter_at | formatDateTime"
              >
                {{ row.record.enter_at | formatTimeWithZone }}
              </span>
            </td>
            <td>
              <template v-if="row.record.exit_at">
                <span
                  v-tooltip
                  :title="row.record.exit_at | formatDateTime"
                >
                  {{ row.record.exit_at | formatTimeWithZone }}
                </span>
              </template>
              <template v-else> - </template>
            </td>
            <td class="duration">{{ row.record.duration | formatDuration('auto', null, false, true) }}</td>
            <template v-if="walker.main_type === 'user' && totalTracked != null">
              <td>&ndash;</td>
              <td>&ndash;</td>
              <td>&ndash;</td>
            </template>
          </template>
        </tr>
      </tbody>
      <tbody class="tfoot">
        <tr>
          <td>Total</td>
          <template v-if="walker.sub_show && walker.third_show">
            <td></td>
          </template>
          <td></td>
          <td></td>
          <td>{{ section.total.duration | formatDuration('auto', null, false, true) }}</td>
          <template v-if="walker.main_type === 'user' && totalTracked != null">
            <td>{{ totalTrackedFor(section.main_id) | formatDuration('auto', null, false, true) }}</td>
            <td>{{ offSiteTimeFor(section) | formatDuration('auto', null, false, true) }}</td>
            <td>
              <template v-if="onSitePercentageFor(section) != null">{{ onSitePercentageFor(section) }}%</template>
              <template v-else>&ndash;</template>
            </td>
          </template>
        </tr>
      </tbody>
    </template>
    <!-- eslint-enable vue/require-v-for-key -->
  </table>
</template>
<script>
import { mapState, mapGetters } from 'vuex';

export default {
  name: 'JobSitesReportTable',
  props: {
    tableData: Object,
  },
  computed: {
    ...mapGetters(['tableIsTeam', 'isOwner', 'tableSumDateRanges', 'walker']),
    ...mapState({
      organization: 'organization',
      totalTracked: 'total_tracked',
    }),
    totalTrackedByMainId() {
      return (this.totalTracked || []).reduce((acc, item) => {
        acc[item.main_id] = (acc[item.main_id] || 0) + item.tracked;

        return acc;
      }, {});
    },
  },
  methods: {
    row_class: function (new_record) {
      return new_record ? 'has-border' : '';
    },
    totalTrackedFor(mainId) {
      return this.totalTrackedByMainId[mainId] || 0;
    },
    offSiteTimeFor(section) {
      return Math.max(0, this.totalTrackedFor(section.main_id) - section.total.duration);
    },
    onSitePercentageFor(section) {
      const totalTracked = this.totalTrackedFor(section.main_id);

      if (totalTracked <= 0 || section.total.duration > totalTracked) {
        return null;
      }

      return Math.round((section.total.duration / totalTracked) * 100);
    },
    format_date_label(value) {
      if (value === 'summed_date') {
        return (
          TimeUtils.format_date(TimeUtils.parse_internal_date(this.$store.state.report_query.date)) +
          ' - ' +
          TimeUtils.format_date(TimeUtils.parse_internal_date(this.$store.state.report_query.date_end))
        );
      }

      return TimeUtils.format_date(TimeUtils.parse_internal_date(value));
    },
  },
};
</script>
